import React from "react";
import PropTypes from "prop-types";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

/* ✅ COLORS */
const COLORS = ["#A9C3DD", "#46d3de", "#5A8DEE", "#C7B9E8", "rgb(0, 177, 144)"];

/* ✅ K / L / Cr FORMATTER */
const formatToCappedUnit = (value) => {
  if (value === null || value === undefined) return "-";
  if (value < 1000) return value.toLocaleString("en-IN");

  if (value < 100000) return `${(value / 1000).toFixed(1)} K`;
  if (value < 10000000) return `${(value / 100000).toFixed(1)} L`;

  return `${(value / 10000000).toFixed(1)} Cr`;
};

/* ✅ PIE LABEL (% TEXT) */
const renderCustomLabel = ({ cx, cy, midAngle, outerRadius, percent }) => {
  const RADIAN = Math.PI / 180;
  const radius = outerRadius + 4;

  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="#333"
      fontSize={11}
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
    >
      {percent.toFixed(1)}%
    </text>
  );
};

renderCustomLabel.propTypes = {
  cx: PropTypes.number,
  cy: PropTypes.number,
  midAngle: PropTypes.number,
  outerRadius: PropTypes.number,
  percent: PropTypes.number,
};

/* ✅ LABEL LINE */
const renderLabelLine = ({ points }) => (
  <polyline points={points} stroke="#999" strokeWidth={1.5} fill="none" />
);

renderLabelLine.propTypes = {
  points: PropTypes.string,
};

/* ✅ TOOLTIP (RAW VALUE ONLY) */
const CustomTooltip = ({ active, payload }) => {
  if (active && payload?.length) {
    const data = payload?.[0]?.payload;

    return (
      <div style={styles.tooltip}>
        <div style={{ fontWeight: 600, marginBottom: "4px" }}>
          {data.name}
        </div>

        <div style={{ color: "#33C1CC" }}>
          Loss Quantity &nbsp;&nbsp;
          {data.value.toLocaleString("en-IN")} {data.suffix}
        </div>

        <div style={{ color: "#5A8DEE" }}>
          Percentage &nbsp;&nbsp; {data.percent}%
        </div>
      </div>
    );
  }
  return null;
};

CustomTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(
    PropTypes.shape({
      payload: PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        suffix: PropTypes.string,
        percent: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      }),
    })
  ),
};

/* ✅ MAIN COMPONENT */
const AllocationToDeliveryLossCategoryGraph = ({ data = [] }) => {
  /* ✅ IMPORTANT FIX */
  const chartData = data.filter((item) => item.value > 0);

  return (
    <div style={styles.container}>
      {/* ✅ LEFT LEGEND */}
      <div style={styles.legend}>
        {data.map((item, index) => (
          <div key={item.name} style={styles.legendItem}>
            <div style={styles.legendLeft}>
              <span
                style={{
                  ...styles.dot,
                  backgroundColor: COLORS[index % COLORS.length],
                }}
              />
              <span style={styles.label}>{item.name}</span>
            </div>

            {/* ✅ HOVER → ACTUAL VALUE */}
            <span
              style={styles.value}
              title={`${item.value.toLocaleString("en-IN")} ${item.suffix}`}
            >
              {`${formatToCappedUnit(item.value)} ${item.suffix}`}
            </span>
          </div>
        ))}
      </div>

      {/* ✅ PIE CHART */}
      <div style={styles.chartWrapper}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart margin={{ top: 0, right: 10, left: 10, bottom: 5 }}>
            <Tooltip content={<CustomTooltip />} />

            <Pie
              data={chartData}
              dataKey="percent"   
              nameKey="name"
              cx="50%"
              cy="82%"
              startAngle={180}
              endAngle={0}
              outerRadius={138}
              innerRadius={96}
              paddingAngle={2}
              label={renderCustomLabel}
              labelLine={renderLabelLine}
            >
              {chartData.map((entry) => (
                <Cell
                  key={entry.name}
                  fill={COLORS[chartData.indexOf(entry) % COLORS.length]}
                />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

AllocationToDeliveryLossCategoryGraph.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      percent: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      suffix: PropTypes.string,
    })
  ),
};

export default AllocationToDeliveryLossCategoryGraph;

/* ✅ STYLES */
const styles = {
  container: {
    display: "flex",
    borderRadius: "12px",
    alignItems: "center",
    height: "250px",
    overflowY: "auto",
    gap: "8px",
  },

  legend: {
    width: "32%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  title: {
    marginBottom: "15px",
    fontSize: "16px",
    fontWeight: "600",
    position: "relative",
    bottom: "30px",
  },


  legendItem: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    alignItems: "center",
    columnGap: "8px",
    padding: "6px 8px",
    marginBottom: "8px",
    marginLeft: "18px",
    background: "#fff",
    borderRadius: "8px",
    border: "1px solid #eee",
    width: "100%",
  },

  dot: {
    width: "7px",
    height: "7px",
    borderRadius: "50%",
    flexShrink: 0,
  },

  legendLeft: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    fontSize: "11px",
  },

  label: {
    fontSize: "12px",
    color: "#333",
    whiteSpace: "normal",
    wordBreak: "break-word",
  },

  value: {
    fontWeight: "600",
    fontSize: "11px",
    textAlign: "right",
    minWidth: "70px",
    // cursor: "pointer",
  },

  chartWrapper: {
    width: "68%",
    height: "100%",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "flex-end",
  },

  tooltip: {
    background: "#fff",
    padding: "8px 10px",
    borderRadius: "8px",
    boxShadow: "0px 2px 10px rgba(0,0,0,0.15)",
    border: "1px solid #eee",
    fontSize: "12px",
  },
};